import React from 'react';
import { attributionCertainty, isRiskAssessed } from '../utils/canvasAssurance';

export interface NodeBBox {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

interface NodeHoverActionsProps {
    node: {
        id: string;
        entity_name?: string;
        entity_type?: string;
        type?: string;
        /** Remaining node data (risk and attribution fields) for the hover line. */
        [key: string]: any;
    };
    bbox: NodeBBox;
    onExpandIn: (nodeId: string) => void;
    onExpandOut: (nodeId: string) => void;
    /** Right-click on the flanking buttons should open the node context menu
     *  (they sit above the canvas and would otherwise swallow the gesture). */
    onNodeContextMenu?: (node: NodeHoverActionsProps['node'], position: { x: number; y: number }) => void;
    /** Called when mouse enters the button area — use to cancel hide timer */
    onMouseEnter?: () => void;
    /** Called when mouse leaves the button area — use to restart hide timer */
    onMouseLeave?: () => void;
}

/**
 * Floating action buttons that appear flanking a hovered canvas node.
 * ← (left)  = expand incoming transactions (funds flowing IN to this address)
 * → (right) = expand outgoing transactions (funds flowing OUT of this address)
 *
 * Positioned using the node's rendered bounding box in viewport coordinates.
 */
export const NodeHoverActions: React.FC<NodeHoverActionsProps> = ({
    node,
    bbox,
    onExpandIn,
    onExpandOut,
    onNodeContextMenu,
    onMouseEnter,
    onMouseLeave,
}) => {
    // Non-address nodes (UPI, labels) don't have expandable blockchain connections
    if (node.type && node.type !== 'address') return null;

    // Scale button to ~50% of node's rendered height so it reads as an
    // accent flanking the node, not a co-star. Clamped so a tiny node
    // (zoom-out) still has a clickable target and a huge node
    // (zoom-in) doesn't get a billboard. Earlier passes used 70% × cap
    // 52 — at typical zoom on a 60px exchange-variant node the buttons
    // hit ~42–52px and read "always large" against the node face.
    const nodeHeight = bbox.y2 - bbox.y1;
    const btnSize = Math.max(20, Math.min(36, Math.round(nodeHeight * 0.5)));
    const halfBtn = btnSize / 2;
    const midY = (bbox.y1 + bbox.y2) / 2;
    // Stem distance: how far the button sits from the node edge. Scales
    // gently with zoom so the visual gap stays proportional. At small
    // zoom the buttons hug the node; at large zoom they have breathing
    // room without floating away.
    const stemPx = Math.max(2, Math.min(10, Math.round(nodeHeight * 0.08)));

    const btnBase =
        'flex items-center justify-center rounded-full shadow-md ' +
        'border-2 border-blue-400 bg-blue-600 text-foreground font-bold select-none ' +
        'transition-transform duration-100 active:scale-95 ';

    const inStyle: React.CSSProperties = {
        position: 'fixed',
        left: bbox.x1 - btnSize - stemPx,
        top: midY - halfBtn,
        width: btnSize,
        height: btnSize,
        // fontSize tracks button size so the arrow glyph stays
        // proportional — was a fixed text-sm before, which read fine at
        // 26px but was lost at 50px and overflowed at 18px.
        fontSize: Math.round(btnSize * 0.55),
        lineHeight: 1,
        zIndex: 9998,
        cursor: 'pointer',
    };

    const outStyle: React.CSSProperties = { ...inStyle, left: bbox.x2 + stemPx };

    const handleButtonContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onNodeContextMenu?.(
            { ...node, id: node.id },
            { x: e.clientX, y: e.clientY },
        );
    };

    // Package 8: say what the card cannot. A node nobody assessed reads "not
    // assessed" rather than looking like a scored-low one, and an attribution
    // shows how it is backed.
    const notAssessed = !isRiskAssessed(node);
    const attribution = attributionCertainty(node);
    const hoverLines: string[] = [];
    if (notAssessed) hoverLines.push('Risk: not assessed');
    if (attribution) hoverLines.push(attribution.text);

    return (
        <>
            {hoverLines.length > 0 && (
                <div
                    data-testid="node-hover-assurance"
                    data-node-id={node.id}
                    className="pointer-events-none rounded bg-card/95 border border-border/70 px-1.5 py-0.5 text-[10px] leading-tight text-muted-foreground shadow"
                    style={{ position: 'fixed', left: bbox.x1, top: bbox.y2 + 4, zIndex: 9998, maxWidth: 260 }}
                >
                    {hoverLines.map(l => <div key={l}>{l}</div>)}
                </div>
            )}
            {/* Expand INCOMING — left side of node */}
            <button
                style={inStyle}
                className={btnBase + 'hover:bg-blue-500'}
                data-node-hover-action="in"
                data-node-id={node.id}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onContextMenu={handleButtonContextMenu}
                onClick={(e) => {
                    e.stopPropagation();
                    onExpandIn(node.id);
                }}
                title="Expand incoming transactions (funds sent TO this address)"
            >
                ←
            </button>

            {/* Expand OUTGOING — right side of node */}
            <button
                style={outStyle}
                className={btnBase + 'hover:bg-blue-500'}
                data-node-hover-action="out"
                data-node-id={node.id}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onContextMenu={handleButtonContextMenu}
                onClick={(e) => {
                    e.stopPropagation();
                    onExpandOut(node.id);
                }}
                title="Expand outgoing transactions (funds sent FROM this address)"
            >
                →
            </button>
        </>
    );
};

export default NodeHoverActions;
